// src/pages/container_rental/components/ExtendRentalDialog.tsx

import { useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  extendSchema,
  ExtendValues,
} from "../../../contracts/container_rental/schemas.ts";

interface ExtendRentalDialogProps {
  open: boolean;
  currentTotal: number;
  onClose: () => void;
  onSubmit: (values: ExtendValues) => Promise<void> | void;
  loading?: boolean;
}

export default function ExtendRentalDialog({
  open,
  currentTotal,
  onClose,
  onSubmit,
  loading = false,
}: ExtendRentalDialogProps) {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ExtendValues>({
    resolver: zodResolver(extendSchema),
    defaultValues: { addRounds: 12 },
  });

  useEffect(() => {
    if (open) reset({ addRounds: 12 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = handleSubmit(async (values) => {
    await onSubmit(values);
  });

  const add = Number(watch("addRounds")) || 0;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="extend-rental-title"
    >
      <DialogTitle id="extend-rental-title">회차 연장</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Controller
            name="addRounds"
            control={control}
            render={({ field }) => (
              <TextField
                label="추가할 회차 수"
                type="number"
                value={field.value ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  field.onChange(v === "" ? "" : Number(v));
                }}
                onBlur={field.onBlur}
                inputRef={field.ref}
                inputProps={{ min: 1 }}
                fullWidth
                error={Boolean(errors.addRounds)}
                helperText={errors.addRounds?.message}
              />
            )}
          />
          <Typography variant="body2" color="text.secondary">
            현재 총 {currentTotal}회차 → 연장 후 총 {currentTotal + (Number.isFinite(add) ? add : 0)}회차
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          취소
        </Button>
        <Button onClick={submit} variant="contained" disabled={loading}>
          {loading ? "처리 중..." : "연장"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
